import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { CheckoutForm, CartItem, PaymentMethod } from '../types';
import CustomDropdown from '../components/CustomDropdown';
import { 
  initializeFlutterwavePayment, 
  initializeFlutterwavePaymentFromData,
  generateTransactionReference, 
  formatAmountForFlutterwave,
  getSupportedPaymentMethods
} from '../utils/flutterwave';
import { createOfflinePayment, redirectToWhatsApp, getPaymentDetails, getPaymentInstructions } from '../utils/offlinePayment';
import { createOnlinePayment } from '../utils/onlinePayment';
import { calculateReferralCommission } from '../utils/referralCommission';
import { OFFLINE_PAYMENT_DETAILS } from '../config/flutterwave';
import {
  ArrowLeftIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import AIChatButton from '../components/AIChatButton';

// Declare Flutterwave types
declare global {
  interface Window {
    FlutterwaveCheckout: (config: any) => void;
  }
}

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    cartItems, 
    getCartTotal, 
    getCartSubtotal,
    clearCart, 
    loading 
  } = useCart();
  const { currentUser } = useAuth();
  
  // Memoize cart-related values to prevent unnecessary re-renders
  const cartItemsLength = useMemo(() => cartItems.length, [cartItems.length]);
  const cartTotal = useMemo(() => getCartTotal(), [cartItems, getCartTotal]);

  // Calculate referral commission if applicable
  const referralCommission = useMemo(() => {
    if (!currentUser?.referredBy) return null;
    
    // Convert CartItem to PaymentItem format for commission calculation
    const paymentItems = cartItems.map(item => ({
      type: item.type,
      id: item.itemId,
      name: item.name,
      amount: item.price
    }));
    
    return calculateReferralCommission(paymentItems, currentUser.referredBy);
  }, [cartItems, currentUser?.referredBy]);

  const [isProcessing, setIsProcessing] = useState(false);
  const [showBillingAddress, setShowBillingAddress] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid },
    setValue,
  } = useForm<CheckoutForm>({
    mode: 'onChange',
    defaultValues: {
      firstName: currentUser?.name?.split(' ')[0] || '',
      lastName: currentUser?.name?.split(' ')[1] || '',
      email: currentUser?.email || '',
      phone: currentUser?.telephone || '',
      address: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'Ghana',
      paymentMethod: 'card',
      agreeToTerms: false,
      savePaymentInfo: false,
    },
  });

  const watchedPaymentMethod = watch('paymentMethod');
  const watchedOfflinePaymentMethod = watch('offlinePaymentMethod');
  
  // Memoize payment methods to prevent unnecessary re-renders
  const paymentMethods = useMemo(() => getSupportedPaymentMethods(), []);

  // Detect if the component is running in an iframe
  const [isInIframe, setIsInIframe] = useState(false);

  useEffect(() => {
    try {
      setIsInIframe(window.self !== window.top);
      console.log('Checkout - Iframe detection:', window.self !== window.top);
    } catch (error) {
      // If we can't access window.top, we're likely in an iframe
      setIsInIframe(true);
      console.log('Checkout - Iframe detected via error handling');
    }
  }, []);

  // Listen for messages from parent window (for iframe scenarios)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'REQUEST_PAYMENT_DATA') {
        // Parent window is requesting payment data
        console.log('Checkout - Received request for payment data from parent');
        // This will be handled when payment is actually processed
      }
    };

    if (isInIframe) {
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }
  }, [isInIframe]);

  useEffect(() => {
    if (!currentUser) {
      navigate('/app/signin', { state: { from: location.pathname } });
      return;
    }

    if (cartItemsLength === 0) {
      navigate('/app/cart');
      return;
    }
  }, [currentUser, cartItemsLength, navigate, location]);



  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
      minimumFractionDigits: 2,
    }).format(price);
  };

  const getItemImage = (item: CartItem) => {
    if (item.image) return item.image;

    switch (item.type) {
      case 'course':
        return 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=150&h=150&fit=crop';
      case 'book':
        return 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=150&h=150&fit=crop';
      case 'video':
        return 'https://images.unsplash.com/photo-1574717024653-61fd2cb4c2be?w=150&h=150&fit=crop';
      default:
        return 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=150&h=150&fit=crop';
    }
  };

  const onSubmit = async (data: CheckoutForm) => {
    if (!currentUser || cartItems.length === 0) {
      toast.error('Invalid checkout data');
      return;
    }

    // Validate offline payment method selection
    if (data.paymentMethod === 'offline' && !data.offlinePaymentMethod) {
      toast.error('Please select an offline payment method');
      return;
    }

    setIsProcessing(true);

    try {
      if (data.paymentMethod === 'offline') {
        // Handle offline payment
        
        // Create offline payment record
        const paymentItems = cartItems.map(item => ({
          type: item.type,
          id: item.itemId,
          name: item.name,
          amount: item.price,
        }));
        
        const paymentId = await createOfflinePayment(
          currentUser.uid,
          paymentItems,
          cartTotal,
          data,
          data.offlinePaymentMethod!,
          currentUser.referredBy || undefined
        );

        // Store order details in localStorage
        const orderData = {
          paymentId,
          items: cartItems,
          total: cartTotal,
          customerInfo: data,
          paymentMethod: 'offline',
          offlinePaymentMethod: data.offlinePaymentMethod,
          referredBy: currentUser.referredBy,
          timestamp: new Date().toISOString(),
        };
        localStorage.setItem('pendingOrder', JSON.stringify(orderData));

        // Redirect to WhatsApp
        redirectToWhatsApp(paymentId, cartTotal, paymentItems, data, currentUser.referredBy || undefined);
        
        // Navigate to offline payment success page
        navigate('/app/offline-payment-success');
      } else {
        // Handle Flutterwave payment
        const transactionRef = generateTransactionReference();

        // Calculate commission if applicable
        const commissionData = referralCommission ? {
          commissionAmount: referralCommission.commissionAmount,
          commissionRate: (referralCommission.commissionAmount / cartTotal) * 100
        } : {};

        // Build meta ensuring only string primitives are passed (Flutterwave requirement)
        const meta = {
          source: 'web',
          userId: String(currentUser.uid),
          orderItems: String(cartItemsLength),
          referredBy: currentUser.referredBy ? String(currentUser.referredBy) : '',
          itemIds: cartItems.map(item => item.itemId).join(','),
          itemTypes: cartItems.map(item => item.type).join(','),
          commissionAmount: referralCommission ? String(referralCommission.commissionAmount) : '',
          commissionRate: referralCommission ? String((referralCommission.commissionAmount / cartTotal) * 100) : '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          postalCode: data.postalCode || '',
          country: data.country || 'Ghana',
        };

        // Payment data for Flutterwave (GHS only)
        const paymentData = {
          tx_ref: transactionRef,
          amount: formatAmountForFlutterwave(cartTotal), // Amount in Ghana Cedis (no conversion needed)
          currency: 'GHS',
          redirect_url: `${window.location.origin}/app/payment-success`,
          customer: {
            email: data.email,
            phone_number: data.phone,
            name: `${data.firstName} ${data.lastName}`,
          },
          customizations: {
            title: 'ProfStudy - Course Purchase',
            description: `Payment for ${cartItemsLength} item(s)`,
            logo: 'https://profstudymateadmin.com/images/icon.png',
          },
          meta,
        };

        console.log('Payment data being sent:', paymentData); // Debug log
        console.log('Checkout - Cart Total (GHS):', cartTotal);
        console.log('Checkout - Amount sent to Flutterwave (pesewas):', formatAmountForFlutterwave(cartTotal));
        console.log('Checkout - userId being sent:', currentUser.uid);
        console.log('Checkout - meta data:', paymentData.meta);
        const response = await initializeFlutterwavePaymentFromData(paymentData);

        if (response) {
                  // Create online payment record in Firebase for tracking
        try {
          // Convert CartItems to PaymentItems
          const paymentItems = cartItems.map(item => ({
            type: item.type,
            id: item.itemId,
            name: item.name,
            amount: item.price
          }));
          
          await createOnlinePayment(
            currentUser.uid,
            paymentItems,
            cartTotal,
            data,
            transactionRef,
            currentUser.referredBy || undefined
          );
          console.log('Online payment record created for tracking');
        } catch (error) {
          console.error('Failed to create online payment record:', error);
          // Continue with payment even if tracking fails
        }

        // Store order details in localStorage for payment success page
        const orderData = {
          transactionRef,
          items: cartItems,
          total: cartTotal,
          customerInfo: data,
          paymentMethod: data.paymentMethod,
          referredBy: currentUser.referredBy,
          timestamp: new Date().toISOString(),
        };
        localStorage.setItem('pendingOrder', JSON.stringify(orderData));

                  // Launch Flutterwave checkout modal with enhanced callback
        if (window.FlutterwaveCheckout) {
          window.FlutterwaveCheckout({
            ...response,
            callback: function(response: any) {
              console.log('Payment callback received:', response);
              
              if (response.status === 'successful') {
                // Payment successful - redirect to success page
                // The success page will handle the URL parameters and create the order
                toast.success('Payment successful! Redirecting...');
                clearCart();
                
                // Check if we're in an iframe
                const isInIframe = window.self !== window.top;
                if (isInIframe) {
                  // Send payment data to parent window via postMessage
                  try {
                    window.parent.postMessage({
                      type: 'PAYMENT_SUCCESS',
                      paymentData: {
                        items: cartItems,
                        total: cartTotal,
                        paymentMethod: data.paymentMethod,
                        paymentReference: transactionRef,
                        transactionId: response.transaction_id || transactionRef,
                        referredBy: currentUser.referredBy,
                        customerInfo: data
                      }
                    }, '*');
                    console.log('Checkout - Payment data sent to parent window via postMessage');
                  } catch (error) {
                    console.error('Checkout - Failed to send message to parent:', error);
                  }
                }
                
                navigate('/app/payment-success');
              } else {
                // Payment failed or cancelled
                toast.error('Payment was not completed. Please try again.');
              }
            },
            onClose: function() {
              console.log('Payment modal closed');
              toast.success('Payment was cancelled');
            }
          });
        } else {
          throw new Error('Flutterwave script not loaded');
        }
        } else {
          throw new Error('Failed to initialize payment');
        }
      }
    } catch (error: any) {
      console.error('Payment processing error:', error);
      
      if (error.message && error.message.includes('Flutterwave script not loaded')) {
        toast.error('Payment system not ready. Please refresh the page and try again.');
      } else {
        toast.error('Failed to process payment. Please try again.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading checkout...</p>
        </div>
      </div>
    );
  }

  if (cartItemsLength === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
          <p className="text-gray-600 mb-6">Add some items to your cart before checking out.</p>
          <Link
            to="/app/catalog"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            Browse Courses
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/app/cart"
            state={{ from: location.pathname }}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Cart
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
          <p className="text-gray-600 mt-2">Complete your purchase securely</p>
        </div>

        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Contact Information</h2>
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Personal Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      {...register('firstName', { required: 'First name is required' })}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.firstName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      {...register('lastName', { required: 'Last name is required' })}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.lastName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      {...register('phone', { 
                        required: 'Phone number is required',
                        pattern: {
                          value: /^(\+233|0)[0-9]{9}$/,
                          message: 'Please enter a valid Ghanaian phone number'
                        }
                      })}
                      placeholder="+233XXXXXXXXX or 0XXXXXXXXX"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                    )}
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Shipping Address</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                        Street Address *
                      </label>
                      <input
                        type="text"
                        id="address"
                        {...register('address', { required: 'Address is required' })}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                          errors.address ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.address && (
                        <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                          City *
                        </label>
                        <input
                          type="text"
                          id="city"
                          {...register('city', { required: 'City is required' })}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            errors.city ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.city && (
                          <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                          State/Region *
                        </label>
                        <input
                          type="text"
                          id="state"
                          {...register('state', { required: 'State is required' })}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            errors.state ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.state && (
                          <p className="mt-1 text-sm text-red-600">{errors.state.message}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-2">
                          Postal Code
                        </label>
                        <input
                          type="text"
                          id="postalCode"
                          {...register('postalCode')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                        Country *
                      </label>
                      <CustomDropdown
                        value={watch('country')}
                        onChange={(value) => setValue('country', value)}
                        options={[
                          { value: "Ghana", label: "Ghana" },
                          { value: "Nigeria", label: "Nigeria" },
                          { value: "Kenya", label: "Kenya" },
                          { value: "South Africa", label: "South Africa" },
                          { value: "Other", label: "Other" }
                        ]}
                        placeholder="Select Country"
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                          errors.country ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.country && (
                        <p className="mt-1 text-sm text-red-600">{errors.country.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Method</h3>
                  
                  <div className="space-y-3">
                    {paymentMethods.map((method) => (
                      <label
                        key={method.id}
                        className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                          watchedPaymentMethod === method.id
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          value={method.id}
                          {...register('paymentMethod', { required: 'Please select a payment method' })}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center">
                            <span className="text-2xl mr-3">{method.icon}</span>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{method.name}</p>
                              <p className="text-sm text-gray-500">{method.description}</p>
                              <p className="text-xs text-gray-400">
                                Processing time: {method.processingTime}
                                {method.fees > 0 && ` • Fees: ${formatPrice(method.fees)}`}
                              </p>
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Offline Payment Method Selection */}
                {watchedPaymentMethod === 'offline' && (
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Offline Payment Method</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {[
                        { key: 'MTN Mobile Money', icon: '📱', color: 'bg-yellow-50 border-yellow-200' },
                        { key: 'MoMo Pay', icon: '📱', color: 'bg-blue-50 border-blue-200' },
                        { key: 'ABSA Bank', icon: '🏦', color: 'bg-green-50 border-green-200' }
                      ].map((method) => (
                        <label
                          key={method.key}
                          className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${method.color} hover:opacity-80`}
                        >
                          <input
                            type="radio"
                            value={method.key}
                            {...register('offlinePaymentMethod', { 
                              required: watchedPaymentMethod === 'offline' ? 'Please select an offline payment method' : false 
                            })}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                          />
                          <div className="ml-3">
                            <span className="text-2xl mr-2">{method.icon}</span>
                            <span className="text-sm font-medium text-gray-900">{method.key}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Offline Payment Instructions */}
                {watchedPaymentMethod === 'offline' && watchedOfflinePaymentMethod && (
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Instructions</h3>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                              <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <span className="text-2xl">{getPaymentDetails(watchedOfflinePaymentMethod as any)?.icon}</span>
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-blue-900">
                              {watchedOfflinePaymentMethod}
                            </h4>
                            <div className="mt-2 space-y-2 text-sm text-blue-800">
                              {watchedOfflinePaymentMethod === 'ABSA Bank' ? (
                              <>
                                <p><strong>Account Number:</strong> {OFFLINE_PAYMENT_DETAILS.absaBank.accountNumber}</p>
                                <p><strong>Account Name:</strong> {OFFLINE_PAYMENT_DETAILS.absaBank.accountName}</p>
                              </>
                            ) : (
                              <>
                                <p><strong>Phone Number:</strong> {OFFLINE_PAYMENT_DETAILS.mtnMobileMoney.phoneNumber}</p>
                                <p><strong>Account Name:</strong> {OFFLINE_PAYMENT_DETAILS.mtnMobileMoney.accountName}</p>
                              </>
                            )}
                            <p><strong>Amount:</strong> {formatPrice(getCartTotal())}</p>
                          </div>
                          <div className="mt-3 p-3 bg-white rounded border border-blue-200">
                            <p className="text-xs text-blue-700 whitespace-pre-line">
                              {getPaymentInstructions(watchedOfflinePaymentMethod as any)}
                            </p>
                          </div>
                          
                          {/* Copy Payment Details Button */}
                          <div className="mt-4">
                            <button
                              type="button"
                              onClick={() => {
                                const paymentDetails = `Payment Details for ${watchedOfflinePaymentMethod}:

${watchedOfflinePaymentMethod === 'ABSA Bank' ? 
  `Account Number: ${OFFLINE_PAYMENT_DETAILS.absaBank.accountNumber}
Account Name: ${OFFLINE_PAYMENT_DETAILS.absaBank.accountName}` : 
  `Phone Number: ${OFFLINE_PAYMENT_DETAILS.mtnMobileMoney.phoneNumber}
Account Name: ${OFFLINE_PAYMENT_DETAILS.mtnMobileMoney.accountName}`
}

Amount: ${formatPrice(cartTotal)}

Instructions:
${getPaymentInstructions(watchedOfflinePaymentMethod as any)}`;

                                navigator.clipboard.writeText(paymentDetails).then(() => {
                                  toast.success('Payment details copied to clipboard!');
                                }).catch(() => {
                                  // Fallback for older browsers
                                  const textArea = document.createElement('textarea');
                                  textArea.value = paymentDetails;
                                  document.body.appendChild(textArea);
                                  textArea.select();
                                  document.execCommand('copy');
                                  document.body.removeChild(textArea);
                                  toast.success('Payment details copied to clipboard!');
                                });
                              }}
                              className="inline-flex items-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              Copy Payment Details
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Terms and Conditions */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="agreeToTerms"
                        type="checkbox"
                        {...register('agreeToTerms', { 
                          required: 'You must agree to the terms and conditions' 
                        })}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="agreeToTerms" className="font-medium text-gray-700">
                        I agree to the{' '}
                        <a href="/legal" className="text-primary-600 hover:text-primary-500">
                          Terms and Conditions
                        </a>{' '}
                        and{' '}
                        <a href="/legal" className="text-primary-600 hover:text-primary-500">
                          Privacy Policy
                        </a>
                      </label>
                      {errors.agreeToTerms && (
                        <p className="mt-1 text-sm text-red-600">{errors.agreeToTerms.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Payment Warning */}
                {watchedPaymentMethod === 'card' && (
                  <div className="border-t border-gray-200 pt-6">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-amber-900 mb-2">
                            ⚠️ Important Payment Notice
                          </h4>
                          <div className="text-sm text-amber-800 space-y-1">
                            <p><strong>Please DO NOT:</strong></p>
                            <ul className="list-disc list-inside ml-2 space-y-1">
                              <li>Close this browser tab or window</li>
                              <li>Close the mobile app</li>
                              <li>Navigate away from the payment page</li>
                              <li>Refresh the page during payment</li>
                            </ul>
                            <p className="mt-2 font-medium">
                              Wait for the payment to complete and you'll be redirected automatically.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="border-t border-gray-200 pt-6">
                  <button
                    type="submit"
                    disabled={!isValid || isProcessing}
                    className="w-full flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    {isProcessing ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Processing...
                      </>
                    ) : watchedPaymentMethod === 'offline' ? (
                      <>
                        <CheckCircleIcon className="h-5 w-5 mr-2" />
                        I Have Made Payment - {formatPrice(cartTotal)}
                      </>
                    ) : (
                      <>
                        <LockClosedIcon className="h-5 w-5 mr-2" />
                        Complete Purchase - {formatPrice(cartTotal)}
                      </>
                    )}
                  </button>
                  
                  <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
                    <ShieldCheckIcon className="h-4 w-4 mr-2" />
                    Your payment information is secure and encrypted
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Order Summary</h2>
              </div>
              
              {/* Cart Items */}
              <div className="space-y-4 mb-6">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex items-center space-x-3">
                    <img
                      src={getItemImage(item)}
                      alt={item.name}
                      className="w-12 h-12 rounded-md object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-1 mb-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                      </div>
                      <p className="text-xs text-gray-500 capitalize">{item.type}</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">{formatPrice(item.price)}</p>
                  </div>
                ))}
              </div>

              {/* Price Breakdown */}
              <div className="border-t border-gray-200 pt-4 space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotal ({cartItems.length} items)</span>
                  <span>{formatPrice(getCartSubtotal())}</span>
                </div>
                
                
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Tax</span>
                  <span>Included</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Processing Fee</span>
                  <span>Free</span>
                </div>
                
                {/* Referral Commission Display */}
                {referralCommission && (
                  <div className="border-t border-gray-200 pt-2">
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Referral Commission (10%)</span>
                      <span>-{formatPrice(referralCommission.commissionAmount)}</span>
                    </div>
                    <div className="text-xs text-green-500 mt-1">
                      Your referrer will earn {formatPrice(referralCommission.commissionAmount)} from this purchase
                    </div>
                  </div>
                )}
                
                <div className="border-t border-gray-200 pt-2">
                  <div className="flex justify-between text-lg font-semibold text-gray-900">
                    <span>Total</span>
                    <span>{formatPrice(getCartTotal())}</span>
                  </div>
                </div>
              </div>

              {/* Security Notice */}
              <div className="mt-6 p-4 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium">Secure Payment</p>
                    <p>Powered by Flutterwave</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* AI Chat Button */}
      <AIChatButton />
    </div>
  );
};

export default Checkout;
